import { useCallback, useState } from 'react';
import {
  apiGet,
  apiPost,
  apiPut,
  apiRemove,
  apiPatch,
  apiAll,
} from 'api-service';
import { isEqualResponse } from 'utils';
import { apis } from 'api-service/apis';

const useApi = () => {
  const [state, setState] = useState({
    loading: false,
    data: [],
    error: null,
    loadingUpdate: false,
    dataUpdate: [],
    errorUpdate: null,
    loadingRemove: false,
    dataRemove: [],
    errorRemove: null,
    loadingEdit: false,
    dataEdit: [],
    errorEdit: null,
    loadingAdd: false,
    dataAdd: [],
    errorAdd: null,
    loadingPatch: false,
    dataPatch: [],
    errorPatch: null,

    loadingAll: false,
    dataAll: [],
    errorAll: null,
  });

  const get = useCallback(
    (
      address,
      params,
      model = null,
      hasMeta,
      render,
      originalResponse,
      customHeader
    ) => {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      return new Promise((resolve, reject) => {
        apiGet(address, params, customHeader)
          .then((response) =>
            originalResponse ? response : response.data.result
          )
          .then((response) => {
            if (response?.data?.length === 0) return response;
            if (model === null) return response;
            return isEqualResponse(response, model);
          })
          .then((response) => {
            return hasMeta ? response : response.data;
          })
          .then((response) => {
            let _newResponse = null;
            if (render) _newResponse = render(response);
            else _newResponse = response;

            setState((prev) => ({
              ...prev,
              loading: false,
              data: _newResponse,
            }));

            resolve(_newResponse);
          })
          .catch((err) => {
            setState((prev) => ({
              ...prev,
              loading: false,
              error: err,
            }));
            reject(err);
          });
      });
    },
    []
  );

  const add = useCallback(
    // eslint-disable-next-line no-unused-vars
    (address, params, model = null, hasMeta, render, originalResponse) => {
      setState((prev) => ({
        ...prev,
        loadingAdd: true,
        errorAdd: null,
      }));
      return new Promise((resolve, reject) => {
        apiPost(address, params)
          .then((response) =>
            originalResponse ? response : response.data.result
          )
          .then((response) => {
            setState((prev) => ({
              ...prev,
              loadingAdd: false,
              dataAdd: response,
            }));
            resolve(response);
          })
          .catch((err) => {
            setState((prev) => ({
              ...prev,
              loadingAdd: false,
              errorAdd: err,
            }));
            reject(err);
          });
      });
    },
    []
  );

  const update = useCallback((address, params) => {
    setState((prev) => ({
      ...prev,
      loadingUpdate: true,
      errorUpdate: null,
    }));
    return new Promise((resolve, reject) => {
      apiPut(address, params)
        .then((response) => response.data.result)
        .then((response) => {
          setState((prev) => ({
            ...prev,
            loadingUpdate: false,
            dataUpdate: response,
          }));
          resolve(response);
        })
        .catch((err) => {
          setState((prev) => ({
            ...prev,
            loadingUpdate: false,
            errorUpdate: err,
          }));
          reject(err);
        });
    });
  }, []);
  const patch = useCallback((address, params) => {
    setState((prev) => ({
      ...prev,
      loadingPatch: true,
      errorPatch: null,
    }));
    return new Promise((resolve, reject) => {
      apiPatch(address, params)
        .then((response) => response.data.result)
        .then((response) => {
          setState((prev) => ({
            ...prev,
            loadingPatch: false,
            dataPatch: response,
          }));
          resolve(response);
        })
        .catch((err) => {
          setState((prev) => ({
            ...prev,
            loadingPatch: false,
            errorPatch: err,
          }));
          reject(err);
        });
    });
  }, []);

  const remove = useCallback((address, params) => {
    setState((prev) => ({
      ...prev,
      loadingRemove: true,
      errorRemove: null,
    }));
    return new Promise((resolve, reject) => {
      apiRemove(address, params)
        .then((response) => response.data.result)
        .then((response) => {
          setState((prev) => ({
            ...prev,
            loadingRemove: false,
            dataRemove: response,
          }));
          resolve(response);
        })
        .catch((err) => {
          setState((prev) => ({
            ...prev,
            loadingRemove: false,
            errorRemove: err,
          }));
          reject(err);
        });
    });
  }, []);

  const all = useCallback((requests, render) => {
    setState((prev) => ({
      ...prev,
      loadingAll: true,
      errorAll: null,
    }));
    return new Promise((resolve, reject) => {
      apiAll(requests)
        .then((response) => {
          let _newResponse = null;
          if (render) _newResponse = render(response);
          else _newResponse = response;

          setState((prev) => ({
            ...prev,
            loadingAll: false,
            dataAll: _newResponse,
          }));

          resolve(_newResponse);
        })
        .catch((err) => {
          setState((prev) => ({
            ...prev,
            loadingAll: false,
            errorAll: err,
          }));
          reject(err);
        });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearData = (_state) => {
    setState((prev) => ({
      ...prev,
      [_state]: null,
    }));
  };

  const updateData = (_state, renderData) => {
    setState((prev) => ({
      ...prev,
      [_state]: renderData(state[_state]),
    }));
  };

  /**
   * دریافت پارامتر های مربوط به یک api
   */
  const getApiParams = useCallback(
    (key) => {
      return apis(key, { get, add, update, remove, patch, all });
    },
    [get, add, update, remove, patch, all]
  );

  return {
    ...state,
    get,
    add,
    all,
    update,
    remove,
    patch,
    clearData,
    getApiParams,
    updateData,
  };
};

export default useApi;
